import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Warning, EnvelopeSimple, X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Critical-path digest banner. Used on the Dashboard (cross-project, multi-project
 * users only) and on the Project Detail page (scoped to that project).
 *
 * Props:
 *   - digest: response from GET /api/projects/critical-path
 *   - projectId: when set, the "Email contractors" button hits the project-scoped
 *                variant of POST /api/projects/critical-path/email-contractors
 *   - onDismiss: optional close handler. When omitted, the dismiss button is hidden.
 *   - canEmailContractors: defaults to true. Pass false for standalone contractors
 *                          who are the only ones working on the project (no PM above them).
 */
export default function CriticalPathBanner({
  digest,
  projectId = null,
  onDismiss,
  canEmailContractors = true,
}) {
  const [busy, setBusy] = useState(false);

  if (!digest) return null;

  const overdue = digest.overdue_items || 0;
  const isAllClear = overdue === 0;

  const hasRecipients = (digest.contractor_emails || []).length > 0;
  const showEmailBtn = !isAllClear && canEmailContractors && hasRecipients;

  const sendEmails = async () => {
    setBusy(true);
    try {
      const params = projectId ? { project_id: projectId } : {};
      const { data } = await api.post("/projects/critical-path/email-contractors", null, { params });
      const skipped = data.skipped || [];
      const skippedNames = skipped.map((s) => s.name).join(", ");
      if (data.sent > 0 && skipped.length === 0) {
        toast.success(`Sent ${data.sent} email${data.sent === 1 ? "" : "s"} to contractors.`);
      } else if (data.sent > 0 && skipped.length > 0) {
        toast.warning(`Sent ${data.sent}, skipped ${skipped.length}.`, {
          description: `No email on file for: ${skippedNames}. Add an email on the contractor record and try again.`,
          duration: 10000,
        });
      } else if (data.sent === 0 && skipped.length > 0) {
        toast.error(`Couldn't email any contractor.`, {
          description: `Missing email for: ${skippedNames}. Add an email on the contractor record and try again.`,
          duration: 10000,
        });
      } else {
        toast.info(data.message || "Nothing to send.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send emails");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border px-5 sm:px-6 py-5 transition-colors ${
        isAllClear
          ? "border-emerald-100 bg-emerald-50/70"
          : "border-red-100 bg-red-50/70"
      }`}
      data-testid="critical-path-banner"
    >
      {onDismiss && (
        <button
          onClick={onDismiss}
          className={`absolute top-3 right-3 p-1.5 rounded-lg transition-colors ${
            isAllClear
              ? "text-emerald-400 hover:text-emerald-700 hover:bg-emerald-100"
              : "text-red-400 hover:text-red-700 hover:bg-red-100"
          }`}
          data-testid="dismiss-critical-path"
          title="Dismiss"
          aria-label="Dismiss critical path banner"
        >
          <X size={14} weight="bold" />
        </button>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 pr-8">
        <div className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center ${
          isAllClear
            ? "bg-emerald-100 text-emerald-600"
            : "bg-red-100 text-red-600"
        }`}>
          {isAllClear ? <CheckCircle size={22} weight="bold" /> : <Warning size={22} weight="bold" />}
        </div>
        <div className="flex-1 min-w-0">
          <span className={`label-eyebrow ${isAllClear ? "text-emerald-700" : "text-red-600"}`}>
            Critical Path
          </span>
          <p
            className="font-display text-base sm:text-lg font-semibold tracking-tight text-[#001E44] mt-1"
            data-testid="critical-path-summary"
          >
            {isAllClear ? "All on track — no overdue procurement items." : digest.summary}
          </p>
          {!isAllClear && digest.items && digest.items.length > 0 && (
            <p className="text-xs text-zinc-500 mt-1 line-clamp-1">
              <span className="font-semibold text-zinc-700">Oldest:</span>{" "}
              {digest.items[0].name}
              {digest.items[0].project_name ? ` · ${digest.items[0].project_name}` : ""}
            </p>
          )}
        </div>
        {showEmailBtn && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={sendEmails}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="email-contractors-button"
            >
              <EnvelopeSimple size={14} weight="bold" />
              {busy ? "Sending…" : "Email contractors"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
